/**
 * useInternalBooking - Hook para criação de agendamento interno
 * 
 * Permite que usuários internos criem agendamentos pelo sistema,
 * com suporte a override de vendedor e skip de validação de slot.
 * Reutiliza a mesma RPC create_scheduling_booking com parâmetros extras v1.8.0.
 * 
 * @version 1.0.0
 * RELEASE: 2026-04-10 v1.0.0 - Implementação inicial
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { getEdgeFunctionUrl } from '@/lib/supabaseHelpers';

const logger = createLogger('InternalBooking');

// ============================================================================
// TIPOS
// ============================================================================

/** Dados para criação de booking interno */
export interface InternalBookingInput {
  /** ID do tipo de evento (obrigatório) */
  eventTypeId: string;
  /** Data/hora de início no formato ISO (obrigatório) */
  startAt: string;
  /** Nome do convidado (obrigatório) */
  guestName: string;
  /** Email do convidado (obrigatório) */
  guestEmail: string;
  /** Telefone do convidado */
  guestPhone?: string;
  /** Observações */
  guestNotes?: string;
  /** Timezone do agendamento */
  timezone?: string;
  /** ID do deal a vincular (se existente) */
  dealId?: string | null;
  /** ID do vendedor (crm_sellers.id) para override do host (v1.8.1) */
  hostSellerId?: string | null;
  /** Pular validação de slot (para agendamento fora do horário disponível) */
  skipSlotValidation?: boolean;
}

// ============================================================================
// HOOK: Mutation para criar booking interno
// ============================================================================

export function useInternalBooking() {
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: async (input: InternalBookingInput) => {
      logger.info('Criando booking interno', {
        eventTypeId: input.eventTypeId,
        startAt: input.startAt,
        dealId: input.dealId,
        hostSellerId: input.hostSellerId || null,
        skipValidation: !!input.skipSlotValidation,
      });

      // Chamada à RPC com parâmetros extras v1.8.0
      const { data, error } = await supabase.rpc('create_scheduling_booking', {
        p_event_type_id: input.eventTypeId,
        p_start_at: input.startAt,
        p_guest_name: input.guestName,
        p_guest_email: input.guestEmail,
        p_guest_phone: input.guestPhone || null,
        p_guest_notes: input.guestNotes || null,
        p_timezone: input.timezone || 'America/Sao_Paulo',
        p_deal_id: input.dealId || null,
        p_host_seller_id: input.hostSellerId || null,
        p_skip_slot_validation: input.skipSlotValidation || false,
      });

      if (error) {
        logger.error('Erro ao criar booking interno', error);
        throw error;
      }

      const bookingId = data as string;

      // Fire-and-forget: chamar scheduling-webhook para criar evento no Calendar + email
      try {
        const webhookUrl = getEdgeFunctionUrl('scheduling-webhook');
        fetch(webhookUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ booking_id: bookingId }),
        }).catch((err) => {
          logger.warn('Erro ao chamar scheduling-webhook (fire-and-forget)', err);
        });
      } catch (webhookErr) {
        logger.warn('Erro ao disparar scheduling-webhook', webhookErr);
      }

      return bookingId;
    },
    onSuccess: () => {
      // Invalidar caches relevantes para atualizar calendário e listas
      queryClient.invalidateQueries({ queryKey: ['calendar-activities'] });
      queryClient.invalidateQueries({ queryKey: ['scheduling-list-activities'] });
      queryClient.invalidateQueries({ queryKey: ['scheduling-kpi'] });
      queryClient.invalidateQueries({ queryKey: ['standalone-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['scheduling-event-types'] });
    },
  });

  return {
    createBooking: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    error: createMutation.error,
  };
}
